/**
 * Minimal IndexedDB wrapper for offline Bible storage. Stores each verse row
 * keyed by `${translation}::${reference}` plus a per-translation manifest so
 * the UI can show what's downloaded.
 *
 * Designed to be usable without any external deps (no idb-keyval).
 */

const DB_NAME = 'lumera-offline-bible';
const DB_VERSION = 2;
const STORE_VERSES = 'verses';
const STORE_MANIFEST = 'manifest';
const STORE_BOOKS = 'books'; // per-book download manifest (translation::book)

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_VERSES)) {
        db.createObjectStore(STORE_VERSES, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_MANIFEST)) {
        db.createObjectStore(STORE_MANIFEST, { keyPath: 'translation' });
      }
      if (!db.objectStoreNames.contains(STORE_BOOKS)) {
        db.createObjectStore(STORE_BOOKS, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(storeName, mode, work) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    try {
      result = work(store);
    } catch (e) {
      reject(e);
      return;
    }
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function storeBulkTranslation(translation, verses) {
  const db = await openDB();
  const t = db.transaction([STORE_VERSES, STORE_MANIFEST], 'readwrite');
  const vstore = t.objectStore(STORE_VERSES);
  const mstore = t.objectStore(STORE_MANIFEST);
  for (const v of verses) {
    vstore.put({
      key: `${translation}::${v.reference}`,
      translation,
      reference: v.reference,
      book: v.book,
      chapter: v.chapter,
      verse: v.verse,
      text: v.text,
    });
  }
  mstore.put({
    translation,
    verse_count: verses.length,
    downloaded_at: new Date().toISOString(),
  });
  return new Promise((resolve, reject) => {
    t.oncomplete = () => resolve(true);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/**
 * Store a single book (all its chapters) for offline reading. Verses share the
 * same key scheme as full-translation downloads, plus a per-book manifest entry
 * so the UI knows which books are saved and getOfflineChapter can serve them.
 */
export async function storeOfflineBook(translation, book, verses, totalChapters = null) {
  const db = await openDB();
  const t = db.transaction([STORE_VERSES, STORE_BOOKS], 'readwrite');
  const vstore = t.objectStore(STORE_VERSES);
  const bstore = t.objectStore(STORE_BOOKS);
  for (const v of verses) {
    vstore.put({
      key: `${translation}::${v.reference}`,
      translation,
      reference: v.reference,
      book: v.book,
      chapter: v.chapter,
      verse: v.verse,
      text: v.text,
    });
  }
  bstore.put({
    key: `${translation}::${book}`,
    translation,
    book,
    verse_count: verses.length,
    total_chapters: totalChapters,
    downloaded_at: new Date().toISOString(),
  });
  return new Promise((resolve, reject) => {
    t.oncomplete = () => resolve(true);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function getOfflineBookManifest(translation, book) {
  return tx(STORE_BOOKS, 'readonly', (store) =>
    new Promise((resolve) => {
      const req = store.get(`${translation}::${book}`);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    })
  );
}

export async function listOfflineBooks() {
  const db = await openDB();
  return new Promise((resolve) => {
    const t = db.transaction(STORE_BOOKS, 'readonly');
    const store = t.objectStore(STORE_BOOKS);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function removeOfflineBook(translation, book) {
  const db = await openDB();
  const t = db.transaction([STORE_VERSES, STORE_BOOKS], 'readwrite');
  const vstore = t.objectStore(STORE_VERSES);
  const bstore = t.objectStore(STORE_BOOKS);
  return new Promise((resolve) => {
    const range = IDBKeyRange.bound(
      `${translation}::`,
      `${translation}::~`,
      false,
      false
    );
    const req = vstore.openCursor(range);
    req.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) {
        bstore.delete(`${translation}::${book}`);
        return;
      }
      // Only delete rows belonging to this book (keep other saved books of the
      // same translation intact).
      if (c.value && c.value.book === book) c.delete();
      c.continue();
    };
    t.oncomplete = () => resolve(true);
    t.onerror = () => resolve(false);
  });
}

export async function getOfflineVerse(translation, reference) {
  return tx(STORE_VERSES, 'readonly', (store) =>
    new Promise((resolve) => {
      const req = store.get(`${translation}::${reference}`);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    })
  ).then((result) => (result instanceof Promise ? result : result));
}

/**
 * Get all verses for a chapter in the user's offline cache.
 * Returns null if the translation isn't downloaded.
 */
export async function getOfflineChapter(translation, book, chapter) {
  // Serve from offline cache when EITHER the whole translation OR just this
  // book has been downloaded.
  const [fullManifest, bookManifest] = await Promise.all([
    getOfflineManifest(translation),
    getOfflineBookManifest(translation, book),
  ]);
  if (!fullManifest && !bookManifest) return null;
  const db = await openDB();
  return new Promise((resolve) => {
    const t = db.transaction(STORE_VERSES, 'readonly');
    const store = t.objectStore(STORE_VERSES);
    const verses = [];
    // Iterate by key prefix is inefficient without an index on (book, chapter),
    // but the dataset fits 31k items comfortably and we only hit this when
    // offline. Use a cursor scan filtered in JS.
    const range = IDBKeyRange.bound(
      `${translation}::`,
      `${translation}::~`, // '~' > 'z' in ASCII, upper bound
      false,
      false
    );
    const req = store.openCursor(range);
    req.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) {
        resolve(verses.length ? verses : null);
        return;
      }
      const row = c.value;
      if (row.book === book && row.chapter === chapter) {
        verses.push({
          reference: row.reference,
          book: row.book,
          chapter: row.chapter,
          verse: row.verse,
          text: row.text,
          translation,
        });
      }
      c.continue();
    };
    req.onerror = () => resolve(null);
  });
}

export async function getOfflineManifest(translation) {
  return tx(STORE_MANIFEST, 'readonly', (store) =>
    new Promise((resolve) => {
      const req = store.get(translation);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    })
  );
}

export async function listOfflineManifests() {
  const db = await openDB();
  return new Promise((resolve) => {
    const t = db.transaction(STORE_MANIFEST, 'readonly');
    const store = t.objectStore(STORE_MANIFEST);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function removeOfflineTranslation(translation) {
  const db = await openDB();
  const t = db.transaction([STORE_VERSES, STORE_MANIFEST], 'readwrite');
  const vstore = t.objectStore(STORE_VERSES);
  const mstore = t.objectStore(STORE_MANIFEST);

  return new Promise((resolve) => {
    // Delete every key starting with "${translation}::"
    const range = IDBKeyRange.bound(
      `${translation}::`,
      `${translation}::~`,
      false,
      false
    );
    const req = vstore.openCursor(range);
    req.onsuccess = (e) => {
      const c = e.target.result;
      if (!c) {
        mstore.delete(translation);
        return;
      }
      c.delete();
      c.continue();
    };
    t.oncomplete = () => resolve(true);
    t.onerror = () => resolve(false);
  });
}

export async function estimateOfflineSize() {
  if (!navigator?.storage?.estimate) return null;
  try {
    const e = await navigator.storage.estimate();
    return { usage: e.usage || 0, quota: e.quota || 0 };
  } catch {
    return null;
  }
}
